import React, { createContext, useContext } from 'react';

import { usePanelActionsInternal } from '../panel-manager/panel-provider';
import type { AllPanels } from '../panel-manager/types';

/**
 * Context that provides the current panel being rendered.
 *
 * Performance note: Each panel instance gets wrapped in PanelContext.Provider
 * when activePanels changes, causing all panels to re-render. This is negligible because:
 * 1. The entire map re-runs anyway (unavoidable)
 * 2. Most apps have <5 panels open simultaneously
 * 3. Panel re-renders are cheap (just UI components)
 * 4. The cleaner API justifies the minimal perf cost
 *
 * For max performance, memoize your panel component:
 * const MyPanel = React.memo(function MyPanel() { ... });
 */
export const PanelContext: React.Context<AllPanels | null> = createContext<AllPanels | null>(null);

/**
 * Hook to access the current panel data being rendered.
 * Must be used within a PanelsRenderer render function that wraps panels in PanelContext.Provider.
 *
 * @example
 * ```tsx
 * <PanelsRenderer>
 *   {(panels) => (
 *     panels.map(panel => (
 *       <PanelContext.Provider key={panel.instanceId} value={panel}>
 *         <MyCustomPanel />
 *       </PanelContext.Provider>
 *     ))
 *   )}
 * </PanelsRenderer>
 *
 * function MyCustomPanel() {
 *   const panel = useCurrentPanel<{ title: string }>();
 *   const closePanel = useCloseCurrentPanel();
 *
 *   return (
 *     <PanelContainer>
 *       <PanelHeader>
 *         <PanelTitle>{panel.params?.title}</PanelTitle>
 *         <PanelActionClose />
 *       </PanelHeader>
 *       <PanelBody>{panel.params?.content}</PanelBody>
 *     </PanelContainer>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function useCurrentPanel<
	TParams extends Record<string, any> = Record<string, any>,
>(): AllPanels & {
	params: TParams;
} {
	const panel = useContext(PanelContext);

	if (!panel) {
		throw new Error(
			'useCurrentPanel must be used within a PanelsRenderer with PanelContext.Provider. ' +
				'Make sure your component is a child of a panel context provider.',
		);
	}

	return panel as AllPanels & { params: TParams };
}

/**
 * Hook to close the current panel.
 * Provides an opinionated way to close the panel without exposing low-level manager API.
 * Must be used within a PanelsRenderer that provides PanelContext.
 *
 * @example
 * ```tsx
 * function MyPanel() {
 *   const closePanel = useCloseCurrentPanel();
 *   // Use closePanel for custom logic, or just use <PanelActionClose /> directly
 *   return (
 *     <PanelActionClose onClick={() => {
 *       trackEvent('panel-closed');
 *       closePanel();
 *     }} />
 *   );
 * }
 * ```
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function useCloseCurrentPanel(): () => void {
	const panel = useCurrentPanel();
	const manager = usePanelActionsInternal();

	return React.useCallback(() => {
		manager.closePanel(panel.instanceId);
	}, [manager, panel.instanceId]);
}

/**
 * Convenience hook that returns both panel data and close function.
 * Reduces boilerplate when you need both in a panel component.
 *
 * @example
 * ```tsx
 * function MyPanel() {
 *   const { panel, closePanel } = usePanel();
 *
 *   return (
 *     <PanelContainer>
 *       <PanelHeader>
 *         <PanelTitle>{panel.params?.title}</PanelTitle>
 *         <PanelActionClose onClick={closePanel} />
 *       </PanelHeader>
 *       <PanelBody>{panel.params?.content}</PanelBody>
 *     </PanelContainer>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function usePanel<TParams extends Record<string, any> = Record<string, any>>(): {
	panel: AllPanels<TParams>;
	closePanel: () => void;
} {
	const panel = useCurrentPanel<TParams>();
	const closePanel = useCloseCurrentPanel();

	return { panel, closePanel };
}
